// src/components/AppBackground.js
// Shared background treatment (carbon weave canvas + LED glow orbs) — extracted
// from HomePage so every page renders the identical effect. Fixed + pointer-events
// none so it never intercepts clicks; pages must render their content with
// position: relative and a z-index above 0 to sit on top of it.
import React, { useEffect, useRef } from 'react';

// Tile size of the animated weave grid — 1.7x HomePage's original 52px so it
// reads as visibly bigger without overwhelming the page.
const GRID = 90;

export default function AppBackground() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let t = 0;

    // Ambient background particles
    const ambient = Array.from({ length: 60 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.4 + 0.3,
      dx: (Math.random() - 0.5) * 0.25,
      dy: (Math.random() - 0.5) * 0.25,
      alpha: Math.random() * 0.35 + 0.08,
      hue: Math.random() > 0.65 ? 'yellow' : Math.random() > 0.4 ? 'teal' : 'white',
    }));

    // Microscopic dust — always visible everywhere, glow + displace near cursor
    const dust = Array.from({ length: 600 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: 0,
      vy: 0,
      r: Math.random() * 0.6 + 0.25,
      baseAlpha: Math.random() * 0.22 + 0.1,
      glowAlpha: 0,
      hue: Math.random() > 0.72 ? 'yellow' : Math.random() > 0.48 ? 'teal' : 'white',
    }));
    dust.forEach(d => {
      d.bx = d.x;
      d.by = d.y;
    });

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const onMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    const onMouseLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 };
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);

    const getColor = (hue, alpha) => {
      if (hue === 'yellow') return `rgba(245,158,11,${alpha})`;
      if (hue === 'teal')   return `rgba(249,115,22,${alpha})`;
      return `rgba(30,27,75,${alpha})`;
    };

    const draw = () => {
      t += 0.012;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ── Carbon wave grid ──
      const cols = Math.ceil(canvas.width / GRID) + 2;
      const rows = Math.ceil(canvas.height / GRID) + 2;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * GRID;
          const y = r * GRID;
          const wave = Math.sin(c * 0.35 + t) * Math.cos(r * 0.28 + t * 0.7) * 0.5 + 0.5;
          const alpha = 0.08 + wave * 0.14;

          // Horizontal line
          ctx.beginPath();
          ctx.strokeStyle = `rgba(30,27,75,${alpha})`;
          ctx.lineWidth = 0.7;
          ctx.moveTo(x, y);
          ctx.lineTo(x + GRID, y);
          ctx.stroke();

          // Vertical line
          ctx.beginPath();
          ctx.strokeStyle = `rgba(30,27,75,${alpha * 0.75})`;
          ctx.lineWidth = 0.7;
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + GRID);
          ctx.stroke();

          // Intersection dot — glowing on wave peaks
          if (wave > 0.65) {
            const dotGlow = (wave - 0.65) / 0.35;
            ctx.beginPath();
            ctx.arc(x, y, 1.4, 0, Math.PI * 2);
            ctx.fillStyle = wave > 0.82
              ? `rgba(245,158,11,${dotGlow * 0.55})`
              : `rgba(249,115,22,${dotGlow * 0.45})`;
            ctx.fill();

            // dot halo
            if (dotGlow > 0.5) {
              const dg = ctx.createRadialGradient(x, y, 0, x, y, 10);
              dg.addColorStop(0, wave > 0.82
                ? `rgba(245,158,11,${dotGlow * 0.2})`
                : `rgba(249,115,22,${dotGlow * 0.18})`);
              dg.addColorStop(1, 'rgba(0,0,0,0)');
              ctx.beginPath();
              ctx.arc(x, y, 10, 0, Math.PI * 2);
              ctx.fillStyle = dg;
              ctx.fill();
            }
          }
        }
      }

      // ── Ambient particles + connections ──
      for (let i = 0; i < ambient.length; i++) {
        const p = ambient[i];

        // connections
        for (let j = i + 1; j < ambient.length; j++) {
          const q = ambient[j];
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < 130) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(249,115,22,${0.055 * (1 - d / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = getColor(p.hue, p.alpha);
        ctx.fill();

        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width)  p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      }

      // ── Microscopic dust: always visible, glow + displace near cursor ──
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const PUSH_RADIUS = 85;
      const GLOW_RADIUS = 150;
      const PUSH_FORCE  = 2.2;

      dust.forEach(d => {
        const ddx  = d.x - mx;
        const ddy  = d.y - my;
        const dist = Math.sqrt(ddx * ddx + ddy * ddy);

        // Push away when close
        if (dist < PUSH_RADIUS && dist > 0) {
          const force = (1 - dist / PUSH_RADIUS) * PUSH_FORCE;
          d.vx += (ddx / dist) * force;
          d.vy += (ddy / dist) * force;
        }

        // Spring back to base position
        d.vx += (d.bx - d.x) * 0.013;
        d.vy += (d.by - d.y) * 0.013;
        d.vx *= 0.87;
        d.vy *= 0.87;
        d.x  += d.vx;
        d.y  += d.vy;

        // Glow based on proximity — smoothly interpolated
        const proximity = dist < GLOW_RADIUS
          ? Math.pow(1 - dist / GLOW_RADIUS, 1.5)
          : 0;
        d.glowAlpha += (proximity * 0.8 - d.glowAlpha) * 0.07;

        // ── Always draw base dot ──
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = getColor(d.hue, d.baseAlpha);
        ctx.fill();

        // ── Glow layer on top when near cursor ──
        if (d.glowAlpha > 0.02) {
          const glowR = d.r + d.glowAlpha * 2;

          // halo
          const g = ctx.createRadialGradient(d.x, d.y, 0, d.x, d.y, glowR * 8);
          if (d.hue === 'yellow') {
            g.addColorStop(0, `rgba(245,158,11,${d.glowAlpha * 0.5})`);
            g.addColorStop(1, 'rgba(245,158,11,0)');
          } else if (d.hue === 'teal') {
            g.addColorStop(0, `rgba(249,115,22,${d.glowAlpha * 0.5})`);
            g.addColorStop(1, 'rgba(249,115,22,0)');
          } else {
            g.addColorStop(0, `rgba(30,27,75,${d.glowAlpha * 0.35})`);
            g.addColorStop(1, 'rgba(30,27,75,0)');
          }
          ctx.beginPath();
          ctx.arc(d.x, d.y, glowR * 8, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();

          // brighter core when glowing
          ctx.beginPath();
          ctx.arc(d.x, d.y, glowR, 0, Math.PI * 2);
          ctx.fillStyle = getColor(d.hue, d.baseAlpha + d.glowAlpha * 0.7);
          ctx.fill();
        }
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <>
      {/* Particle canvas — weave grid + ambient particles + cursor-follow dust */}
      <canvas ref={canvasRef} style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }} />

      {/* Ambient LED glow orbs */}
      <div style={{
        position: 'fixed',
        top: '20%',
        left: '15%',
        width: 500,
        height: 500,
        background: 'radial-gradient(circle, rgba(249,115,22,0.07) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0,
        animation: 'glowPulse 6s ease-in-out infinite',
      }} />
      <div style={{
        position: 'fixed',
        top: '50%',
        right: '10%',
        width: 400,
        height: 400,
        background: 'radial-gradient(circle, rgba(245,158,11,0.05) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0,
        animation: 'glowPulse 8s ease-in-out infinite 2s',
      }} />

      <style>{`
        @keyframes glowPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%       { opacity: 0.7; transform: scale(1.05); }
        }
      `}</style>
    </>
  );
}
